import { isCloseEqual } from '@src/slide/application/service/snap-line-service/util/index';
import type { OperationAnchor } from '@src/slide/application/service/snap-line-service/interface/orient.interface';

export type Vector = [number, number];

/**
 * 根据 mode 获取 锚点方向向量
 */
export function getVectorWithAnchor(anchor: OperationAnchor): Vector {
    let verticalVector = 0;
    if (anchor.indexOf('top') !== -1) {
        verticalVector = 1;
    } if (anchor.indexOf('bottom') !== -1) {
        verticalVector = -1;
    }

    let horizontalVector = 0;
    if (anchor.indexOf('left') !== -1) {
        horizontalVector = -1;
    } if (anchor.indexOf('right') !== -1) {
        horizontalVector = 1;
    }

    return [horizontalVector, verticalVector];
}

/**
 * 根据向量获取锚点
 */
export function getAnchorWithVector(vector: Vector): OperationAnchor {
    const [horizontalVector, verticalVector] = vector;
    const modesArray: string[] = [];

    if (horizontalVector !== 0) {
        modesArray.push(horizontalVector > 0 ? 'right' : 'left');
    }
    if (verticalVector !== 0) {
        modesArray.push(verticalVector > 0 ? 'top' : 'bottom');
    }

    return modesArray.join('-') as OperationAnchor;
}

/**
 * 获取旋转后的向量
 */
export function getRotateVector(vector: Vector, rotate: number): Vector {
    const { sinRadius, cosRadius } = getTrigonometricResultByRotate(rotate);
    const rotatedX = (vector[0] * cosRadius) + (vector[1] * sinRadius);
    const rotatedY = (-vector[0] * sinRadius)  + (vector[1] * cosRadius);

    return [rotatedX, rotatedY];
}

/**
 * 获取三角函数结果的近似值
 */
export function getTrigonometricResultByRotate(rotate: number) {
    const angleRad = rotate * Math.PI / 180;
    let cosRadius = Math.cos(angleRad);
    if (isCloseEqual(cosRadius, 0)) {
        cosRadius = 0;
    }
    let sinRadius = Math.sin(angleRad);
    if (isCloseEqual(sinRadius, 0)) {
        sinRadius = 0;
    }

    return { cosRadius, sinRadius };
}


/**
 * 计算向量A和向量B的点积
 */
export function getPointsProduct(vectorA: Vector, vectorB: Vector) {
    return (vectorA[0] * vectorB[0]) + (vectorA[1] * vectorB[1]);
}

/**
 * 计算向量的平方长度
 */
export function getVectorSquaredLength(vector: Vector) {
    return (vector[0] * vector[0]) + (vector[1] * vector[1]);
}

/**
 * 计算向量A在向量B上的投影向量
 */
export function getVectorProjection(vectorA: Vector, vectorB: Vector) {
    const factor = getPointsProduct(vectorA, vectorB) / getVectorSquaredLength(vectorB);
    return [factor * vectorB[0], factor * vectorB[1]];
}

/**
 * 通过直角边和斜边向量，计算另一直角边的长度
 */
export function calculateOtherSideLength(
    sideLength: number,
    diagonalVector: Vector,
    isSideHorizontal: boolean,
): number {
    const vectorLength =  Math.sqrt((diagonalVector[0] * diagonalVector[0])
        + (diagonalVector[1] * diagonalVector[1]));
    const unitVector = [diagonalVector[0] / vectorLength, diagonalVector[1] / vectorLength];
    const diagonalLength = isSideHorizontal ? sideLength / unitVector[0] : sideLength / unitVector[1];
    return Math.sqrt((diagonalLength * diagonalLength) - (sideLength * sideLength));
}
